import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { entityCreationRegistry } from '../../../services/entityCreationRegistry';

export const bookProjectCreateCommand: SelfContainedCommand = {
  id: 'book_project_create',
  description: 'Create a new Book project and open it in the Book Formatter',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Title for the book (default: "My Book")',
        required: false,
        defaultValue: 'My Book'
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'The ID of the space to create the book in',
        required: false
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'The ID of the folder to place the book in. Leave empty for root level.',
        required: false
      },
      {
        name: 'createWindow',
        type: 'boolean',
        description: 'Whether to open the book after creating it. Default is true.',
        required: false,
        defaultValue: true
      }
    ]
  },

  examples: {
    good: [
      {
        input: { name: 'My Novel' },
        expectedOutput: createSuccessResult('Book created successfully: "My Novel"', {}),
        description: 'Create a new book project'
      }
    ],
    bad: []
  },

  performance: {
    expectedDuration: 500,
    complexity: 'low'
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { name = 'My Book', spaceId: providedSpaceId, folderId, createWindow = true } = params;
    const spaceId = providedSpaceId || context.spaceId;

    try {
      const now = new Date().toISOString();
      const entityData = {
        entityType: 'BookProject',
        name: name.trim(),
        spaceId,
        skeleton: {
          '@type': 'BookProject',
          title: name.trim(),
          author: '',
          formatSettings: {
            pageSize: '6x9',
            orientation: 'portrait',
            margins: { top: 0.75, bottom: 0.75, inside: 0.75, outside: 0.5 },
            bodyFont: 'Garamond',
            bodyFontSize: 11,
            lineHeight: 1.5,
            headingFont: 'Garamond',
            headingFontSize: 16,
            chapterPageBreak: true,
            header: { enabled: false, content: '', position: 'top' },
            footer: { enabled: true, content: 'pageNumber', position: 'bottom' },
            tableOfContents: { enabled: true, style: 'standard' }
          },
          chapters: [],
          groupIds: folderId ? [folderId] : [],
          createdAt: now,
          updatedAt: now
        },
        created_at: now
      };

      const entityStoreModule = await import('../../../stores/entityStore');
      const newBook = await entityStoreModule.default.getState().actions.createEntity(entityData as any);
      const entityId = newBook._id || newBook.id;

      if (folderId && entityId) {
        try {
          const { default: useDataspaceTreeStore } = await import('../../../stores/dataspaceTreeStore');
          const treeStore = useDataspaceTreeStore.getState();
          treeStore._applyAddNode(`${folderId}:${entityId}`, {
            id: entityId,
            parentId: folderId,
            pos: 'M',
            expanded: false,
            kind: 'canonical'
          });
        } catch (treeError) {
          console.warn('[book_project_create] Failed to add to tree (non-fatal):', treeError);
        }
      }

      let windowInfo = null;
      if (createWindow !== false && spaceId && entityId) {
        try {
          const { useCommandRegistryStore } = await import('../../../stores/commandRegistryStore');
          const result = (await useCommandRegistryStore.getState().executeCommand('space_open_window', {
            entityId
          })) as StandardToolResult;

          if (result.status === 'success') {
            windowInfo = {
              id: result.data?.windowId,
              title: name.trim(),
              appType: 'book_formatter_v2',
              entityId
            };
          }
        } catch (windowError) {
          console.error('[book_project_create] Failed to open window (non-fatal):', windowError);
        }
      }

      return createSuccessResult(
        `Book created successfully: "${name.trim()}"`,
        {
          entityId,
          name: newBook.name,
          entityType: 'BookProject',
          window: windowInfo
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            affectedEntities: [entityId]
          } as any
        }
      );
    } catch (error: any) {
      return createErrorResult(
        'Book creation failed',
        `Failed to create book: ${error.message || 'Unknown error'}`,
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

entityCreationRegistry.register({
  commandId: 'book_project_create',
  entityType: 'BookProject',
  displayName: 'Book',
  icon: {
    type: 'phosphor',
    phosphorIcon: 'BookOpen',
    phosphorWeight: 'duotone'
  },
  description: 'Create a book and open it in the Book Formatter',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'document',
  sortOrder: 10
});
